/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Localizer from '../../helpers/localization';
import AccessibleButton from '../buttons/AccessibleButton';
import { PictoPlay } from '../pictos/Element';
import './WatchingStatus.css';

const ONE_HUNDRED = 100;
const DEFAULT_GRANULARITY = 1;
const TWO = 2;
const RADIUS = 40;
const TRANSITION_TIMEOUT = 50;

// Circumference is ~251.3
const CIRCUMFERENCE = Math.PI * RADIUS * TWO;

// Ensures that minimum progress is displayed when a program has been watched for a very short time (~ CIRCUMFERENCE - 13)
const MAX_CIRCUMFERENCE_PERCENTAGE = 238;

type WatchingStatusPropType = {
  +allowZeroProgress?: boolean,
  +granularity?: number,
  +onClick?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => void,
  +progress?: number | null,
};

const WatchingStatus = ({ allowZeroProgress = false, granularity = DEFAULT_GRANULARITY, onClick, progress = null }: WatchingStatusPropType): React.Node => {
  const transitionTimer = useRef<TimeoutID | null>(null);

  const [percentage, setPercentage] = useState<number | null>(null);

  // Only contains a cleanup function
  useEffect(
    () => () => {
      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
      }
    },
    [],
  );

  const update = useCallback(() => {
    if (progress === undefined || progress === null) {
      // If progress is null, nothing to show
      return;
    }

    let adjustedProgress = 0;

    if (progress > ONE_HUNDRED) {
      adjustedProgress = ONE_HUNDRED;
    } else if (progress > 0) {
      adjustedProgress = Math.floor(granularity * Math.round(progress / granularity));
    }

    const newPercentage =
      adjustedProgress > 0 || !allowZeroProgress ? Math.min(((ONE_HUNDRED - adjustedProgress) / ONE_HUNDRED) * CIRCUMFERENCE, allowZeroProgress ? CIRCUMFERENCE : MAX_CIRCUMFERENCE_PERCENTAGE) : null;

    if (percentage === null && newPercentage !== null) {
      // First set percentage to CIRCUMFERENCE to allow CSS transition to play
      setPercentage(CIRCUMFERENCE);

      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
      }
      transitionTimer.current = setTimeout(() => setPercentage(newPercentage), TRANSITION_TIMEOUT);
    }
  }, [allowZeroProgress, granularity, percentage, progress]);

  useEffect(() => {
    update();
  }, [progress, update]);

  const progressCircle = percentage !== null ? <circle cx='50' cy='50' r={RADIUS} style={{ strokeDashoffset: percentage }} /> : null;

  return (
    <AccessibleButton
      aria-label={onClick !== undefined ? Localizer.localize('accessibility.common.watch') : undefined}
      className={clsx('watchingStatus', onClick !== undefined && 'clickable')}
      onClick={onClick}
    >
      <svg aria-hidden viewBox='0 0 100 100'>
        <circle className='background' cx='50' cy='50' r={RADIUS} />
        {progressCircle}
      </svg>
      <PictoPlay aria-hidden className='play' />
    </AccessibleButton>
  );
};

export default WatchingStatus;
